import settings from '@/constants/constants';
import { FF_DISABLE_CONSULTATION_BUTTONS } from '@/constants/featureFlags';
import ffDefaults from '@/constants/ffDefaults';

import { ConsultEligibleCalendarProvider, useConsultEligibleCalendar } from '@/context/calendar-context';
import { PreScreeningContextProvider } from '@/context/pre-screening-context';
import { getConsultEligibleNurseSteps } from '@/data/ConsultEligibleNurse/consult-eligible-nurse-steps';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import { WithPageWrapper } from '@/pages/components/Branding';
import SystemUnavailable from '@/pages/components/SystemUnavailable';
import { PRESCREENING_FORM_EMAIL_LOCAL_STORAGE_KEY } from '@/pages/PreScreeningForms/PreScreeningForm';
import { getSearchParams } from '@/utils/url';
import { FormBuilder } from '@montugroup/form-builder';
import { Box, CircularProgress } from '@mui/material';
import type { FieldValues } from 'react-hook-form';
import Calendar from './components/Calendar';

type Props = {
  _callback?: (event: string, data: FieldValues) => void;
  onSubmit?: (data: FieldValues) => Promise<void>;
  pageWrapper?: boolean;
};

const ConsultEligibleContent = ({ onSubmit }: Props) => {
  const { flags, loading } = useFeatureFlags();
  const { showCalendar } = useConsultEligibleCalendar();

  const userEmail = getSearchParams('email', {
    localStorageKey: PRESCREENING_FORM_EMAIL_LOCAL_STORAGE_KEY,
  });

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          height: '100vh',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress color="inherit" />
      </Box>
    );
  }

  const disableConsultations = flags[FF_DISABLE_CONSULTATION_BUTTONS] ?? ffDefaults[FF_DISABLE_CONSULTATION_BUTTONS];

  if (disableConsultations) {
    return <SystemUnavailable />;
  }

  const component = showCalendar ? (
    <Calendar showCalendar={showCalendar} emailAddress={userEmail ?? undefined} />
  ) : (
    <FormBuilder
      formId="consult-eligible"
      steps={getConsultEligibleNurseSteps()}
      postUrl={`${settings.url}/consult-eligible`}
      onSubmit={onSubmit}
    />
  );

  return <PreScreeningContextProvider>{WithPageWrapper(component, true)}</PreScreeningContextProvider>;
};

export default function ConsultEligibleNurse({ onSubmit }: Props) {
  return (
    <ConsultEligibleCalendarProvider>
      <PreScreeningContextProvider>
        <ConsultEligibleContent onSubmit={onSubmit} />
      </PreScreeningContextProvider>
    </ConsultEligibleCalendarProvider>
  );
}
